import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Container,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
  getPublicQuote,
  acceptPublicQuote,
  rejectPublicQuote,
  downloadPublicQuotePdf,
} from '../../services/api';
import type { QuoteStop, QuotePriceLine } from '../../types';

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const cargoTypeLabels: Record<string, string> = {
  PALLET_GOODS: 'Pallegods', GENERAL_CARGO: 'Stykgods', COOL: 'Køl', FREEZE: 'Frost',
  BULK_CARGO: 'Bulk', CONTAINER: 'Container', MACHINES: 'Maskiner', PACKAGES: 'Pakker', MISCELLANEOUS: 'Diverse',
};

function StopCard({ stop, index }: { stop: QuoteStop; index: number }) {
  const place = stop.place;
  const addressParts = [
    place?.placeName, place?.address,
    [place?.postcode, place?.city].filter(Boolean).join(' '),
    place?.country,
  ].filter(Boolean);

  return (
    <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, mb: 1 }}>
      <Typography variant="body2" fontWeight={600}>
        {index + 1}. {addressParts.join(', ') || 'Adresse ikke angivet'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
        {stop.date && <Typography variant="caption" color="text.secondary">Dato: {stop.date}</Typography>}
        {stop.time && <Typography variant="caption" color="text.secondary">Tid: {stop.time}</Typography>}
        {stop.loadmeters && <Typography variant="caption" color="text.secondary">LDM: {stop.loadmeters}</Typography>}
        {stop.weight && <Typography variant="caption" color="text.secondary">Vægt: {stop.weight} kg</Typography>}
      </Box>
    </Box>
  );
}

export default function PublicQuoteView() {
  const { token } = useParams<{ token: string }>();
  const [comment, setComment] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<'accept' | 'reject' | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { data: quote, isLoading, error, refetch } = useQuery({
    queryKey: ['public-quote', token],
    queryFn: () => getPublicQuote(token!),
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptPublicQuote(token!, comment || undefined),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Tilbuddet er accepteret! Tak for din tilbagemelding.', severity: 'success' });
      setConfirmDialog(null);
      refetch();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke acceptere tilbuddet';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setConfirmDialog(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectPublicQuote(token!, comment || undefined),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Tilbuddet er afvist.', severity: 'success' });
      setConfirmDialog(null);
      refetch();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke afvise tilbuddet';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setConfirmDialog(null);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !quote) {
    return (
      <Container maxWidth="sm" sx={{ pt: 10 }}>
        <Alert severity="error">Tilbuddet blev ikke fundet eller linket er ugyldigt.</Alert>
      </Container>
    );
  }

  const isSent = quote.status === 'SENT';
  const isAccepted = quote.status === 'ACCEPTED';
  const isRejected = quote.status === 'REJECTED';
  const isExpired = quote.validUntil && new Date(quote.validUntil) < new Date();

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        {/* Company header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <LocalShippingIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700} color="primary">
            {quote.company.name}
          </Typography>
        </Box>

        {/* Status banners */}
        {isAccepted && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Du har accepteret dette tilbud den {formatDate(quote.acceptedAt)}.
          </Alert>
        )}
        {isRejected && (
          <Alert severity="error" icon={<CancelIcon />} sx={{ mb: 2 }}>
            Du har afvist dette tilbud den {formatDate(quote.rejectedAt)}.
          </Alert>
        )}
        {isSent && isExpired && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tilbuddet er udløbet den {formatDate(quote.validUntil)}.
          </Alert>
        )}

        {/* Quote info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Tilbud #{quote.quoteNumber}</Typography>
              <Chip
                label={
                  isAccepted ? 'Accepteret' :
                  isRejected ? 'Afvist' :
                  isExpired ? 'Udløbet' : 'Sendt'
                }
                color={isAccepted ? 'success' : isRejected ? 'error' : isExpired ? 'warning' : 'info'}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Sendt til: {quote.customerName} ({quote.customerEmail})
            </Typography>
            {quote.validUntil && (
              <Typography variant="body2" color="text.secondary">
                Gyldigt til: {formatDate(quote.validUntil)}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Customer message (intro) */}
        {quote.customerMessage && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{quote.customerMessage}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Transport details */}
        {(quote.cargoType || quote.description) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Transportoplysninger</Typography>
              {quote.cargoType && <Typography variant="body2">Godstype: {cargoTypeLabels[quote.cargoType] || quote.cargoType}</Typography>}
              {quote.description && <Typography variant="body2">Beskrivelse: {quote.description}</Typography>}
              {quote.adr && <Typography variant="body2">ADR: Ja{quote.adrText ? ` — ${quote.adrText}` : ''}</Typography>}
            </CardContent>
          </Card>
        )}

        {/* Stops — iterér over loads; server har filtreret skjulte væk */}
        {(() => {
          const loads = quote.loads && quote.loads.length > 0
            ? quote.loads
            : [{ pickups: quote.pickups, dropoffs: quote.dropoffs, cargoType: quote.cargoType, description: quote.description }];
          const multi = loads.length > 1;

          return loads.map((load, loadIdx) => {
            const hasAny = (load.pickups?.length || 0) + (load.dropoffs?.length || 0) > 0;
            if (!hasAny) return null;
            return (
              <Box key={loadIdx} sx={{ mb: 3 }}>
                {multi && (
                  <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                    Læs {loadIdx + 1}
                    {load.cargoType && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>· {cargoTypeLabels[load.cargoType] || load.cargoType}</Typography>}
                  </Typography>
                )}
                <Grid container spacing={3}>
                  {(load.pickups?.length || 0) > 0 && (
                    <Grid item xs={12} md={(load.dropoffs?.length || 0) > 0 ? 6 : 12}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Afhentninger</Typography>
                          {(load.pickups || []).map((stop, i) => <StopCard key={i} stop={stop} index={i} />)}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {(load.dropoffs?.length || 0) > 0 && (
                    <Grid item xs={12} md={(load.pickups?.length || 0) > 0 ? 6 : 12}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Afleveringer</Typography>
                          {(load.dropoffs || []).map((stop, i) => <StopCard key={i} stop={stop} index={i} />)}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          });
        })()}

        {/* Price lines */}
        {quote.priceLines.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Prisspecifikation</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Beskrivelse</TableCell>
                    <TableCell align="right">Antal</TableCell>
                    <TableCell>Enhed</TableCell>
                    <TableCell align="right">Beløb</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quote.priceLines.map((line: QuotePriceLine, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{line.description}</TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell>{line.unitName || 'stk'}</TableCell>
                      <TableCell align="right">{formatCurrency(line.amount * line.quantity, quote.currency)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3}><strong>Total</strong></TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="primary">
                        {formatCurrency(quote.totalAmount, quote.currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Terms */}
        {(quote.terms || quote.note) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Vilkår</Typography>
              {quote.terms && <Typography variant="body2">{quote.terms}</Typography>}
              {quote.note && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 1 }}>
                  Note: {quote.note}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions (only for SENT status, not expired) */}
        {isSent && !isExpired && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Svar på tilbud</Typography>
              <TextField
                label="Kommentar (valgfrit)"
                fullWidth
                multiline
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setConfirmDialog('accept')}
                  disabled={acceptMutation.isPending || rejectMutation.isPending}
                  size="large"
                >
                  Acceptér tilbud
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setConfirmDialog('reject')}
                  disabled={acceptMutation.isPending || rejectMutation.isPending}
                  size="large"
                >
                  Afvis tilbud
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => downloadPublicQuotePdf(token!, quote.quoteNumber)}
                >
                  Download PDF
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* PDF download for already responded */}
        {(isAccepted || isRejected) && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              startIcon={<PictureAsPdfIcon />}
              onClick={() => downloadPublicQuotePdf(token!, quote.quoteNumber)}
            >
              Download PDF
            </Button>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            DORA Insight — insight.dora-apps.com
          </Typography>
        </Box>
      </Container>

      {/* Confirm dialogs */}
      <Dialog open={confirmDialog === 'accept'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Acceptér tilbud?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Du accepterer tilbud #{quote.quoteNumber} på <strong>{formatCurrency(quote.totalAmount, quote.currency)}</strong> fra {quote.company.name}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => acceptMutation.mutate()}
            disabled={acceptMutation.isPending}
          >
            {acceptMutation.isPending ? <CircularProgress size={20} /> : 'Acceptér'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'reject'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Afvis tilbud?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Er du sikker på, at du vil afvise tilbud #{quote.quoteNumber}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => rejectMutation.mutate()}
            disabled={rejectMutation.isPending}
          >
            {rejectMutation.isPending ? <CircularProgress size={20} /> : 'Afvis'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
